// TutorEmailForm — inline "Add option" form for the tutor_email category.
// The admin types an email; the server normalises it, derives the row key and records who added it.
import { useState } from "react";
import { parseTutorEmail } from "@dm-lms/shared/validations";
import { ERROR_CODES, TUTOR_EMAIL_MAX_LENGTH } from "@dm-lms/shared/constants";
import type { SystemConfig } from "@dm-lms/shared/types";
import { postJson } from "../../hooks/useApi.js";
import type { AddOptionFormProps } from "./AddOptionForm.js";

const INPUT_CLASS =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal";

export default function TutorEmailForm({ sortOrder, onAdded, onCancel }: AddOptionFormProps) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    const parsed = parseTutorEmail(email);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await postJson<SystemConfig>("/api/config", {
        category:   "tutor_email",
        label:      parsed.email,
        sort_order: sortOrder,
      });
      if (!res.ok) {
        setError(res.code === ERROR_CODES.ALREADY_EXISTS ? "That email is already listed." : res.error);
        return;
      }
      setEmail("");
      onAdded();
    } catch {
      setError("Unable to add email. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={(event) => { void handleSubmit(event); }} className="mb-4 rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div>
        <label htmlFor="tutor-email" className="block text-sm font-medium text-dm-navy">Tutor email</label>
        <input
          id="tutor-email"
          type="email"
          required
          maxLength={TUTOR_EMAIL_MAX_LENGTH}
          value={email}
          onChange={(event) => { setEmail(event.target.value); }}
          className={INPUT_CLASS}
        />
        <p className="mt-1 text-xs text-dm-slate">Saved in lowercase. To correct a typo, remove the entry and add it again.</p>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-dm-navy px-4 py-2 text-sm font-medium text-white transition hover:bg-dm-blue disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Adding…" : "Add"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-dm-navy"
        >
          Cancel
        </button>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}
